import React, { createContext, useContext, useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import { SocketType } from "./types/socketContextType";
import { UserId } from "../data_models/helpers";
import { EUserType } from "../data_models/enums";
import { ChatMessage, ChatOneToOneConversation } from "../data_models/chat";
import { Invitation } from "../data_models/invitation";

import { ArielleResponse } from "../data_models/aiModels/arielleResponseModels";
import { ArielleRequest } from "../data_models/aiModels/arielleRequestModels";

// Create the Socket Context
export const SocketContext = createContext<SocketType | undefined>(undefined);

// Define the provider component
export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [socket, setSocket] = useState<Socket>();
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [reconnectAttempts, setReconnectAttempts] = useState<number>(0);
    const MAX_IMMEDIATE_RECONNECT_ATTEMPTS = 3;
    const RECONNECT_DELAY = 2000; // 2 seconds

    const url = window.env?.BACKEND_SOCKET || 'http://localhost:8080';

    const attemptReconnect = (socket: Socket) => {
        if (reconnectAttempts < MAX_IMMEDIATE_RECONNECT_ATTEMPTS) {
            setReconnectAttempts(prev => prev + 1);
            setTimeout(() => {
                console.log(`Attempting reconnection (${reconnectAttempts + 1}/${MAX_IMMEDIATE_RECONNECT_ATTEMPTS})...`);
                socket.connect();
            }, RECONNECT_DELAY);
        } else {
            console.log('Maximum immediate reconnection attempts reached. Falling back to built-in reconnection.');
            setReconnectAttempts(0);
        }
    };

    useEffect(() => {
        try {
            // Initialize the socket connection
            const newSocket: Socket = io(url, {
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000,
                timeout: 5000,
            });

            // Set the socket instance in state
            setSocket(newSocket);

            // Handle connection events
            newSocket.on("connect", () => {
                console.log('Socket connected successfully');
                setIsConnected(true);
                setReconnectAttempts(0);
            });

            newSocket.on("connect_error", (error) => {
                console.error("Connection error:", error.message);
                setIsConnected(false);
            });

            newSocket.on("disconnect", (reason) => {
                console.error(`Disconnected from server. Reason: ${reason}`);
                setIsConnected(false);
                
                // Handle different disconnect reasons
                switch (reason) {
                    case 'io server disconnect':
                        // Server initiated disconnect, try to reconnect with delay
                        attemptReconnect(newSocket);
                        break;
                    case 'io client disconnect':
                        // Client initiated disconnect, don't reconnect automatically
                        console.log('Client initiated disconnect, not reconnecting');
                        break;
                    case 'ping timeout':
                    case 'transport close':
                    case 'transport error':
                        // Network related issues, let the built-in reconnection handle it
                        console.log('Network issue detected, attempting reconnection...');
                        break;
                    default:
                        // For any other reason, try to reconnect with delay
                        attemptReconnect(newSocket);
                }
            });

            // Cleanup on unmount
            return () => {
                newSocket.disconnect();
            };

        } catch (error) {
            console.error('SocketProvider error:', error);
        }

    }, []);

    const disconnectSocket = () => {
        socket?.disconnect();
        setIsConnected(false);
    }


    const registerUser = (userId: string, userType: EUserType) => {
        socket?.emit("register_user_socket", userId, userType);
    }

    const listenToMessages = (callback: (message: ChatMessage) => void) => {
        socket?.on("receive_private_message", (message: ChatMessage) => {
            callback(message);
        });
    }

    const listenToOneToOneConversations = (callback: (conversation: ChatOneToOneConversation) => void) => {
        socket?.on("receive_private_message_conversation", (conversation: ChatOneToOneConversation) => {
            callback(conversation);
        });
    }

    const listenToInvitations = (callback: (Invitation: Invitation) => void) => {
        socket?.on("receive_task_invitation", (Invitation: Invitation) => {
            callback(Invitation);
        });
    }

    const listenToTyping = (callback: (sender: UserId, conversationId: string) => void) => {
        socket?.on("currently_typing", (sender: UserId, conversationId: string) => {
            callback(sender, conversationId);
        });
    }

    const listenToArielle = (callback: (arielleMessage: ArielleResponse) => void) => {
        socket?.on("arielle_response", (message: ArielleResponse) => {
            callback(message);
        });
    }

    const sendToArielle = (message: ArielleRequest) => {
        console.log("sendToArielle", message);
        socket?.emit("arielle_request", message);
    }

    return <SocketContext.Provider value={{
        socket,
        isConnected,
        disconnectSocket,
        registerUser,
        listenToMessages,
        listenToOneToOneConversations,
        listenToInvitations,
        listenToTyping,
        listenToArielle,
        sendToArielle
    } as SocketType}>{children}</SocketContext.Provider>;
};

